import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSiteConfig } from "@/context/SiteConfigContext";
import request, { getSinglePage } from "@/utils/api";

// API 基础 URL（用于图片预览）
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// 注册字段类型
export type FieldType = "text" | "textarea" | "select" | "radio" | "checkbox" | "password" | "number" | "date" | "file" | "image";

export interface RegisterField {
  field_name: string;
  field_label: string;
  field_type: FieldType;
  placeholder: string;
  hint?: string;
  options?: string[];
  is_required: boolean;
  is_enabled: boolean;
  sort_order: number;
}

// 获取当前站点ID
const getSiteId = () => localStorage.getItem('current_site_id') || '';

// 获取注册配置
async function getRegisterConfig(siteId: string): Promise<{ fields: RegisterField[]; sms_enabled?: boolean }> {
  return request<{ fields: RegisterField[]; sms_enabled?: boolean }>(`/register/config`, {
    params: { site_id: siteId },
  });
}

// 注册参数接口
export interface RegisterParams {
  username: string;
  password: string;
  site_id: string;
  phone?: string;
  sms_code?: string;
  [key: string]: string | undefined;
}

// 注册函数
async function registerApi(params: RegisterParams) {
  return request("/auth/register", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

// 发送短信验证码
async function sendSmsCode(siteId: string, phone: string): Promise<void> {
  return request("/register/send-code", {
    method: "POST",
    params: { site_id: siteId },
    body: { phone },
  });
}

// 文件上传函数
async function uploadFile(file: File): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append("file", file);
  // 注意：不要手动设置 Content-Type，浏览器会自动添加正确的 multipart/form-data 及 boundary
  return request("/upload", {
    method: "POST",
    body: formData,
  });
}

export default function RegisterPage() {
  const [fields, setFields] = useState<RegisterField[]>([]);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [uploadingFields, setUploadingFields] = useState<Record<string, boolean>>({});
  const { register } = useAuth();
  const navigate = useNavigate();
  const formDataRef = useRef<Record<string, string>>({});
  const { siteConfig } = useSiteConfig();

  // 从后台获取 logo 和网站名称
  const siteLogo = siteConfig?.logo || "https://public.readdy.ai/ai/img_res/1ebe5b90-bc3d-41ad-9f51-693742fb28f5.png";
  const siteDisplayName = siteConfig?.site_name || siteConfig?.name || "商服汇";
  
  // 协议弹窗相关
  const [agreementModal, setAgreementModal] = useState<{ show: boolean; title: string; content: string; loading: boolean }>({
    show: false, title: "", content: "", loading: false,
  });

  // 打开协议弹窗并加载内容
  const openAgreement = useCallback(async (slug: string, title: string) => {
    setAgreementModal({ show: true, title, content: "", loading: true });
    try {
      const page = await getSinglePage(slug);
      setAgreementModal({
        show: true,
        title: page?.title || title,
        content: page?.content || "暂无内容",
        loading: false,
      });
    } catch {
      setAgreementModal({ show: true, title, content: "加载失败，请稍后重试", loading: false });
    }
  }, []);

  const closeAgreement = useCallback(() => {
    setAgreementModal({ show: false, title: "", content: "", loading: false });
  }, []);

  // 短信验证相关
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [smsLoading, setSmsLoading] = useState(false);

  // 加载注册配置
  useEffect(() => {
    async function loadConfig() {
      try {
        const config = await getRegisterConfig(getSiteId());
        // 过滤出启用的字段
        const enabledFields = config.fields.filter((f) => f.is_enabled);
        
        // 初始化表单数据
        const initialData: Record<string, string> = {};
        enabledFields.forEach((field) => {
          initialData[field.field_name] = "";
        });
        
        // 设置短信验证开关
        if (config.sms_enabled) {
          initialData['phone'] = '';
          initialData['sms_code'] = '';
          setSmsEnabled(true);
        }
        
        // 一次性更新两个状态
        setFields(enabledFields);
        setFormData(initialData);
        formDataRef.current = initialData;
      } catch (err) {
        console.error("加载注册配置失败:", err);
        setError("加载表单配置失败，请刷新重试");
      } finally {
        setIsLoadingConfig(false);
      }
    }
    loadConfig();
  }, []);
  
  // 短信倒计时
  useEffect(() => {
    if (smsCountdown > 0) {
      const timer = setTimeout(() => setSmsCountdown(smsCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [smsCountdown]);
  
  // 发送短信验证码
  const handleSendSmsCode = useCallback(async () => {
    const phone = formDataRef.current['phone'] || '';
    
    if (!phone) {
      setError("请先输入手机号");
      return;
    }
    
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setError("手机号格式不正确");
      return;
    }
    
    try {
      setSmsLoading(true);
      await sendSmsCode(getSiteId(), phone);
      setSmsCountdown(60);
      setError("");
    } catch (err: any) {
      setError(err.message || "短信发送失败，请重试");
    } finally {
      setSmsLoading(false);
    }
  }, []);

  // 处理输入变化
  const handleChange = useCallback((fieldName: string, value: string) => {
    setFormData((prev) => {
      const newData = { ...prev, [fieldName]: value };
      formDataRef.current = newData;
      return newData;
    });
  }, []);

  // 切换密码显示
  const togglePasswordVisibility = useCallback((fieldName: string) => {
    setShowPasswords((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
  }, []);

  // 处理文件上传
  const handleFileUpload = useCallback(async (fieldName: string, file: File) => {
    try {
      setUploadingFields((prev) => ({ ...prev, [fieldName]: true }));
      const result = await uploadFile(file);
      handleChange(fieldName, result.url);
    } catch (err) {
      console.error("文件上传失败:", err);
      alert("文件上传失败，请重试");
    } finally {
      setUploadingFields((prev) => ({ ...prev, [fieldName]: false }));
    }
  }, []);

  // 获取图标
  const getFieldIcon = (fieldType: FieldType) => {
    switch (fieldType) {
      case "text":
      case "number":
        return "ri-edit-line";
      case "textarea":
        return "ri-file-text-line";
      case "select":
      case "radio":
      case "checkbox":
        return "ri-list-check";
      case "password":
        return "ri-lock-line";
      case "date":
        return "ri-calendar-line";
      case "file":
      case "image":
        return "ri-image-add-line";
      default:
        return "ri-edit-line";
    }
  };

  // 渲染字段
  const renderField = (field: RegisterField) => {
    // 使用 formDataRef.current 确保显示最新值
    const value = formDataRef.current[field.field_name] || "";
    const isPassword = field.field_type === "password";
    const showPassword = showPasswords[field.field_name] || false;
    const baseInputClass = `w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent`;

    switch (field.field_type) {
      case "textarea":
        return (
          <textarea
            key={field.field_name}
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            placeholder={field.placeholder || field.hint || `请输入${field.field_label}`}
            rows={3}
            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent resize-none"
          />
        );

      case "select":
        return (
          <select
            key={field.field_name}
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            className={baseInputClass}
          >
            <option value="">{field.placeholder || `请选择${field.field_label}`}</option>
            {field.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case "radio":
        return (
          <div key={field.field_name} className="space-y-2">
            {field.options?.map((option) => (
              <label
                key={option}
                className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${
                  value === option
                    ? "border-orange-200 bg-orange-50"
                    : "border-gray-200 bg-gray-50"
                }`}
              >
                <input
                  type="radio"
                  name={field.field_name}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleChange(field.field_name, e.target.value)}
                  className="w-4 h-4 text-orange-500"
                />
                <span className="text-sm">{option}</span>
              </label>
            ))}
          </div>
        );

      case "checkbox":
        return (
          <div key={field.field_name} className="space-y-2">
            {field.options?.map((option) => (
              <label
                key={option}
                className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  name={field.field_name}
                  value={option}
                  checked={value.split(",").includes(option)}
                  onChange={(e) => {
                    const currentValues = value ? value.split(",") : [];
                    const newValues = e.target.checked
                      ? [...currentValues, option]
                      : currentValues.filter((v) => v !== option);
                    handleChange(field.field_name, newValues.join(","));
                  }}
                  className="w-4 h-4 text-orange-500 rounded"
                />
                <span className="text-sm">{option}</span>
              </label>
            ))}
          </div>
        );

      case "date":
        return (
          <input
            key={field.field_name}
            type="date"
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            className={baseInputClass}
          />
        );

      case "number":
        return (
          <input
            key={field.field_name}
            type="number"
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            placeholder={field.placeholder || field.hint || `请输入${field.field_label}`}
            className={baseInputClass}
          />
        );

      case "file":
      case "image":
        return (
          <div key={field.field_name}>
            <label
              htmlFor={`file-${field.field_name}`}
              className="flex flex-col items-center justify-center w-full px-4 py-6 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:bg-orange-50 hover:border-orange-300 transition-colors"
            >
              {uploadingFields[field.field_name] ? (
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 border-4 border-orange-400 border-t-transparent rounded-full animate-spin mb-2"></div>
                  <span className="text-sm text-gray-500">上传中...</span>
                </div>
              ) : value ? (
                <div className="flex flex-col items-center">
                  {field.field_type === "image" ? (
                    <img 
                      src={value.startsWith('/uploads') ? value : value} 
                      alt="预览" 
                      className="w-24 h-24 object-cover rounded-lg mb-2"
                      onError={(e) => {
                        console.error('图片加载失败:', value);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <i className="ri-file-check-line text-3xl text-green-500 mb-2"></i>
                  )}
                  <span className="text-sm text-gray-600">点击重新上传</span>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <i className="ri-upload-cloud-2-line text-3xl text-gray-400 mb-2"></i>
                  <span className="text-sm text-gray-500">{field.placeholder || `点击上传${field.field_type === "image" ? "图片" : "文件"}`}</span>
                  {field.hint && <span className="text-xs text-gray-400 mt-1">{field.hint}</span>}
                </div>
              )}
            </label>
            <input
              id={`file-${field.field_name}`}
              type="file"
              accept={field.field_type === "image" ? "image/*" : "*/*"}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleFileUpload(field.field_name, file);
                }
              }}
              className="hidden"
            />
            {value && (
              <button
                type="button"
                onClick={() => handleChange(field.field_name, "")}
                className="mt-2 text-sm text-red-500 hover:text-red-600"
              >
                清除
              </button>
            )}
          </div>
        );

      default:
        // text 或 password
        return (
          <div className="relative">
            <input
              key={field.field_name}
              type={isPassword ? (showPassword ? "text" : "password") : "text"}
              value={value}
              onChange={(e) => handleChange(field.field_name, e.target.value)}
              placeholder={field.placeholder || field.hint || `请输入${field.field_label}`}
              className={isPassword ? `${baseInputClass} pr-11` : baseInputClass}
            />
            {isPassword && (
              <button
                type="button"
                onClick={() => togglePasswordVisibility(field.field_name)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className={showPassword ? "ri-eye-line" : "ri-eye-off-line"} />
              </button>
            )}
          </div>
        );
    }
  };

  // 表单验证
  const validate = () => {
    const currentData = formDataRef.current;
    
    // 检查协议
    if (!agreed) {
      setError("请先阅读并同意注册协议和隐私政策");
      return false;
    }

    // 检查必填字段
    for (const field of fields) {
      if (field.is_required && !(currentData[field.field_name] || "").trim()) {
        setError(`${field.field_label}不能为空`);
        return false;
      }
    }
    
    // 短信验证（如果启用）
    if (smsEnabled) {
      const phone = currentData['phone'] || '';
      const smsCode = currentData['sms_code'] || '';
      
      if (!phone) {
        setError("请输入手机号");
        return false;
      }
      
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        setError("手机号格式不正确");
        return false;
      }
      
      if (!smsCode) {
        setError("请输入短信验证码");
        return false;
      }
      
      if (smsCode.length !== 6) {
        setError("短信验证码为6位数字");
        return false;
      }
    }

    // 特殊验证：密码确认（跳过此验证，由后端处理）
    // 前端只需要确保两个字段都有值即可

    // 验证用户名格式
    const usernameField = fields.find((f) => f.field_name === "username");
    if (usernameField && currentData.username) {
      if (!/^\w{3,20}$/.test(currentData.username)) {
        setError("用户名只能包含字母、数字和下划线，长度3-20位");
        return false;
      }
    }

    // 验证密码长度
    if (currentData.password && currentData.password.length < 6) {
      setError("密码长度至少6位");
      return false;
    }

    return true;
  };

  // 提交表单
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!validate()) return;

    setIsLoading(true);
    try {
      const currentData = formDataRef.current;
      
      // 构建注册参数（不发送昵称字段，直接用用户名作为昵称）
      const params: RegisterParams = {
        username: currentData.username,
        password: currentData.password,
        site_id: getSiteId(),
        confirm_password: currentData.confirm_password || '',
      };

      // 添加自定义字段（排除已处理的字段和 nickname）
      fields.forEach((field) => {
        if (!["username", "password", "nickname", "confirm_password"].includes(field.field_name)) {
          params[field.field_name] = currentData[field.field_name] ?? '';
        }
      });
      
      // 添加短信验证码参数（如果启用）
      if (smsEnabled) {
        params.phone = currentData['phone'];
        params.sms_code = currentData['sms_code'];
      }

      // 调用注册API
      await registerApi(params);

      // 注册成功，跳转到首页
      navigate("/");
    } catch (err: any) {
      setError(err.message || "注册失败，请稍后重试");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingConfig) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-orange-500" />
          <p className="mt-2 text-gray-500 text-sm">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-orange-500 transition-colors"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-left-line text-base" />
            </div>
            返回首页
          </Link>
          <Link to="/" className="flex items-center gap-2">
            {siteLogo && (
              <img
                src={siteLogo}
                alt={siteDisplayName}
                className="h-8 w-8 object-contain"
              />
            )}
            <span className="text-lg font-bold text-gray-800">{siteDisplayName}</span>
          </Link>
        </div>

        {/* Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">创建账户</h1>
          <p className="text-gray-500 text-sm mt-1">加入{siteDisplayName}，发现优质商业服务</p>
        </div>

        {/* Register Form */}
        <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 动态渲染表单字段 */}
            {fields.map((field) => (
              <div key={field.field_name}>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {field.field_label}
                  {field.is_required && <span className="text-red-500">*</span>}
                </label>
                <div className="relative">
                  <i
                    className={`${getFieldIcon(field.field_type)} absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400`}
                  />
                  {renderField(field)}
                </div>
                {field.hint && (
                  <p className="mt-1 text-xs text-gray-400">{field.hint}</p>
                )}
              </div>
            ))}
            
            {/* 短信验证码（如果启用） */}
            {smsEnabled && (
              <>
                {/* 手机号 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    手机号 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <i className="ri-phone-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      value={formData['phone'] || ''}
                      onChange={(e) => {
                        handleChange('phone', e.target.value);
                        setError("");
                      }}
                      placeholder="请输入手机号"
                      className="w-full pl-10 pr-24 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={handleSendSmsCode}
                      disabled={smsCountdown > 0 || smsLoading}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 px-3 py-1.5 text-xs bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-300 disabled:text-gray-500 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {smsLoading ? '发送中...' : smsCountdown > 0 ? `${smsCountdown}s后重发` : '获取验证码'}
                    </button>
                  </div>
                </div>
                
                {/* 验证码 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    短信验证码 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <i className="ri-shield-check-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={formData['sms_code'] || ''}
                      onChange={(e) => {
                        // 只允许数字
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        handleChange('sms_code', value);
                        setError("");
                      }}
                      placeholder="请输入6位验证码"
                      maxLength={6}
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent tracking-widest"
                    />
                  </div>
                </div>
              </>
            )}

            {/* 错误提示 */}
            {error && (
              <div className="p-3 bg-red-50 text-red-600 text-sm rounded-xl flex items-center gap-2">
                <i className="ri-error-warning-line shrink-0" />
                {error}
              </div>
            )}

            {/* 协议勾选 */}
            <div
              className={`flex items-start gap-2.5 p-3 rounded-xl border transition-colors cursor-pointer ${
                agreed ? "border-orange-200 bg-orange-50" : "border-gray-200 bg-gray-50"
              }`}
              onClick={() => setAgreed(!agreed)}
            >
              <div
                className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
                  agreed ? "border-orange-500 bg-orange-500" : "border-gray-300 bg-white"
                }`}
              >
                {agreed && <i className="ri-check-line text-white text-xs" />}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed select-none">
                我已阅读并同意
                <span
                  className="text-orange-500 hover:underline mx-1 cursor-pointer"
                  onClick={(e) => { e.stopPropagation(); openAgreement("register-agreement", "注册协议"); }}
                >
                  《注册协议》
                </span>
                和
                <span
                  className="text-orange-500 hover:underline mx-1 cursor-pointer"
                  onClick={(e) => { e.stopPropagation(); openAgreement("privacy", "隐私政策"); }}
                >
                  《隐私政策》
                </span>
              </p>
            </div>

            {/* 提交按钮 */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-orange-500 text-white font-medium rounded-xl hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />
                  注册中...
                </span>
              ) : (
                "立即注册"
              )}
            </button>
          </form>

          <div className="mt-5 text-center">
            <p className="text-sm text-gray-500">
              已有账户？{" "}
              <Link to="/login" className="text-orange-600 font-medium hover:text-orange-700">
                立即登录
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* ── 协议弹窗 ── */}
      {agreementModal.show && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={closeAgreement}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 头部 */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-file-text-line text-orange-500" />
                {agreementModal.title}
              </h2>
              <button
                onClick={closeAgreement}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            {/* 内容 */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {agreementModal.loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="text-center">
                    <i className="ri-loader-4-line animate-spin text-2xl text-orange-400" />
                    <p className="mt-2 text-sm text-gray-400">加载中...</p>
                  </div>
                </div>
              ) : (
                <div
                  className="prose prose-sm max-w-none text-gray-600 leading-relaxed whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: agreementModal.content }}
                />
              )}
            </div>
            {/* 底部按钮 */}
            <div className="px-6 py-4 border-t border-gray-100 shrink-0 flex justify-end">
              <button
                onClick={closeAgreement}
                className="px-6 py-2.5 bg-orange-500 text-white font-medium rounded-xl hover:bg-orange-600 transition-colors text-sm cursor-pointer"
              >
                已了解，关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
